import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  type CSSProperties,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import type { TutorialStep } from './types';

function clippedRect(el: Element): DOMRect | null {
  const r = el.getBoundingClientRect();
  const top = Math.max(0, r.top);
  const left = Math.max(0, r.left);
  const bottom = Math.min(window.innerHeight, r.bottom);
  const right = Math.min(window.innerWidth, r.right);
  if (bottom <= top || right <= left) return null;
  return new DOMRect(left, top, right - left, bottom - top);
}

const GAP = 16;
const CARD_W = 320;

function computePosition(
  anchor: DOMRect,
  placement: string,
  cardH: number
): { top: number; left: number; actualSide: string } {
  const cx = anchor.left + anchor.width / 2;
  const cy = anchor.top + anchor.height / 2;
  let top: number;
  let left: number;
  let actualSide = placement;

  switch (placement) {
    case 'top': {
      top = anchor.top - GAP - cardH;
      left = cx - CARD_W / 2;
      if (top < 8) {
        top = anchor.bottom + GAP;
        actualSide = 'bottom';
      }
      break;
    }
    case 'bottom': {
      top = anchor.bottom + GAP;
      left = cx - CARD_W / 2;
      if (top + cardH > window.innerHeight - 8) {
        top = anchor.top - GAP - cardH;
        actualSide = 'top';
      }
      break;
    }
    case 'left': {
      top = cy - cardH / 2;
      left = anchor.left - GAP - CARD_W;
      if (left < 8) {
        left = anchor.right + GAP;
        actualSide = 'right';
      }
      break;
    }
    default: {
      // right
      top = cy - cardH / 2;
      left = anchor.right + GAP;
      if (left + CARD_W > window.innerWidth - 8) {
        left = anchor.left - GAP - CARD_W;
        actualSide = 'left';
      }
      break;
    }
  }

  top = Math.max(8, Math.min(top, window.innerHeight - cardH - 8));
  left = Math.max(8, Math.min(left, window.innerWidth - CARD_W - 8));

  return { top, left, actualSide };
}

interface CoachmarkProps {
  step: TutorialStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  isVisible: boolean;
  marcusMessage?: string;
}

export function Coachmark({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  isVisible,
  marcusMessage,
}: CoachmarkProps) {
  const prefersReducedMotion = useReducedMotion();
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardH, setCardH] = useState(200);
  const scrollingIntoViewRef = useRef(false);

  useLayoutEffect(() => {
    if (!isVisible) return;
    const el = document.querySelector(step.targetSelector);
    if (!el) {
      setAnchor(null);
      return;
    }
    const r = clippedRect(el);
    const full = el.getBoundingClientRect();
    const sufficient =
      r && r.height >= full.height * 0.5 && r.width >= full.width * 0.5;

    setAnchor(sufficient ? r : null);
  }, [isVisible, step.targetSelector]);

  useEffect(() => {
    if (!isVisible) return;

    let cancelled = false;
    let raf = 0;
    let observer: MutationObserver | undefined;

    const pollUntilVisible = (el: Element) => {
      const start = performance.now();
      let scrollAttempt = 0;
      scrollingIntoViewRef.current = true;
      const tick = () => {
        if (cancelled) {
          scrollingIntoViewRef.current = false;
          return;
        }
        const elapsed = performance.now() - start;
        if (elapsed > 3000) {
          scrollingIntoViewRef.current = false;
          return;
        }
        const r = clippedRect(el);
        const full = el.getBoundingClientRect();
        const sufficient =
          r && r.height >= full.height * 0.5 && r.width >= full.width * 0.5;
        if (sufficient) {
          scrollingIntoViewRef.current = false;

          setAnchor(r);
        } else {
          if (scrollAttempt === 0 && elapsed > 300) {
            scrollAttempt = 1;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else if (scrollAttempt === 1 && elapsed > 1800) {
            scrollAttempt = 2;
            el.scrollIntoView({ block: 'center' });
          }
          raf = requestAnimationFrame(tick);
        }
      };
      raf = requestAnimationFrame(tick);
    };

    const el = document.querySelector(step.targetSelector);
    if (el) {
      const clipped = clippedRect(el);
      const full = el.getBoundingClientRect();
      const sufficientlyVisible =
        clipped &&
        clipped.height >= full.height * 0.5 &&
        clipped.width >= full.width * 0.5;
      if (sufficientlyVisible) return;
      pollUntilVisible(el);
    } else {
      observer = new MutationObserver(() => {
        const found = document.querySelector(step.targetSelector);
        if (found) {
          observer?.disconnect();
          observer = undefined;
          const rect = clippedRect(found);
          const full = found.getBoundingClientRect();
          const sufficient =
            rect &&
            rect.height >= full.height * 0.5 &&
            rect.width >= full.width * 0.5;
          if (sufficient) {
            setAnchor(rect);
          } else {
            pollUntilVisible(found);
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      cancelled = true;
      scrollingIntoViewRef.current = false;
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [isVisible, step.targetSelector]);

  useEffect(() => {
    if (!isVisible) return;
    const update = () => {
      if (scrollingIntoViewRef.current) return;
      const el = document.querySelector(step.targetSelector);
      if (el) setAnchor(clippedRect(el));
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    // Brief rAF polling to track elements mid-CSS-transition (drawer slide-in).
    const settleStart = performance.now();
    let raf = requestAnimationFrame(function tick() {
      if (performance.now() - settleStart > 500) return;
      update();
      raf = requestAnimationFrame(tick);
    });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isVisible, step.targetSelector]);

  useEffect(() => {
    if (cardRef.current) {
      setCardH(cardRef.current.offsetHeight);
    }
  });

  const isCenter = step.placement === 'center';
  const isLastStep = stepIndex === totalSteps - 1;

  if (isCenter) {
    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, scale: prefersReducedMotion ? 1 : 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: prefersReducedMotion ? 1 : 0.95 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
            className="fixed inset-0 z-[301] flex items-center justify-center"
          >
            <CoachmarkContent
              step={step}
              stepIndex={stepIndex}
              totalSteps={totalSteps}
              onNext={onNext}
              onPrev={onPrev}
              onSkip={onSkip}
              isLastStep={isLastStep}
              marcusMessage={marcusMessage}
            />
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  if (!anchor) return null;

  const { top, left, actualSide } = computePosition(
    anchor,
    step.placement,
    cardH
  );

  const arrowStyle = getArrowStyle(actualSide, anchor, top, left, cardH);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={cardRef}
          initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: prefersReducedMotion ? 0 : 4 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
          className="fixed z-[301]"
          style={{ top, left, width: CARD_W }}
        >
          <CoachmarkContent
            step={step}
            stepIndex={stepIndex}
            totalSteps={totalSteps}
            onNext={onNext}
            onPrev={onPrev}
            onSkip={onSkip}
            isLastStep={isLastStep}
            marcusMessage={marcusMessage}
          />
          <div
            className="bg-chariot-layer1 fixed size-3 rotate-45"
            style={arrowStyle}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function getArrowStyle(
  side: string,
  anchor: DOMRect,
  cardTop: number,
  cardLeft: number,
  cardHeight: number
): CSSProperties {
  const cx = anchor.left + anchor.width / 2;
  const cy = anchor.top + anchor.height / 2;
  const clampX = Math.max(
    cardLeft + 16,
    Math.min(cx - 6, cardLeft + CARD_W - 16)
  );
  const clampY = Math.max(
    cardTop + 16,
    Math.min(cy - 6, cardTop + cardHeight - 16)
  );

  switch (side) {
    case 'top':
      return { left: clampX, top: cardTop + cardHeight - 4 };
    case 'bottom':
      return { left: clampX, top: cardTop - 4 };
    case 'left':
      return { left: cardLeft + CARD_W - 4, top: clampY };
    default:
      return { left: cardLeft - 4, top: clampY };
  }
}

function CoachmarkContent({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  isLastStep,
  marcusMessage,
}: {
  step: TutorialStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  isLastStep: boolean;
  marcusMessage?: string;
}) {
  return (
    <div
      className={cn(
        'border-chariot-stroke/20 bg-chariot-layer1 text-chariot-text-primary w-80 rounded-lg border p-4 shadow-lg'
      )}
      role="dialog"
      aria-label={step.title}
    >
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{step.title}</h3>
        <span className="text-chariot-text-secondary text-xs">
          {stepIndex + 1} / {totalSteps}
        </span>
      </div>

      {marcusMessage && (
        <div className="text-chariot-text-primary bg-chariot-layer2 mb-2 rounded p-2 text-xs italic">
          {marcusMessage}
        </div>
      )}

      <div className="text-chariot-text-secondary mb-4 text-sm">
        {step.body}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onSkip}>
          Skip
        </Button>
        <div className="flex gap-2">
          {stepIndex > 0 && (
            <Button variant="outline" size="sm" onClick={onPrev}>
              Back
            </Button>
          )}
          {step.advanceOn !== 'click-target' && (
            <Button size="sm" onClick={onNext}>
              {isLastStep ? 'Done' : 'Next'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
